import { Link } from 'react-router-dom';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import SectionHeading from '@/components/feature/SectionHeading';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { specials } from '@/mocks/specials';
import { useSeo } from '@/hooks/useSeo';

const movieCount = movies.filter((m) => m.type === 'movie').length;
const tvCount = movies.filter((m) => m.type === 'tv').length;

const latest = [...movies].sort((a, b) => b.year - a.year).slice(0, 8);
const popular = [...movies].sort((a, b) => b.views - a.views).slice(0, 8);

function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return String(views);
}

export default function Home() {
  useSeo(
    '十八禁影视大全-最新十八禁电影电视剧推荐',
    '十八禁提供丰富的影视内容，涵盖最新十八禁电影和电视剧，帮助用户了解相关影视作品及资讯。',
  );

  return (
    <div className="w-full">
      <HeroCarousel />

      {/* 分类快捷入口 */}
      <section className="w-full px-4 md:px-8 pt-10 md:pt-14 relative z-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          <Link
            to="/category?type=movie"
            className="group relative h-40 md:h-48 rounded-lg overflow-hidden border border-background-200/70"
          >
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20dark%20film%20reel%20and%20projector%20light%20in%20a%20theater%2C%20deep%20crimson%20and%20charcoal%20color%20grade%2C%20dramatic%20low-key%20lighting%2C%20no%20text&width=800&height=400&seq=sexcat-movie&orientation=landscape"
              alt="十八禁电影分类入口"
              title="十八禁电影分类入口"
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/60 to-transparent" />
            <div className="absolute inset-0 p-6 flex flex-col justify-center">
              <span className="w-11 h-11 rounded-md bg-primary-500/90 flex items-center justify-center mb-3">
                <i className="ri-film-line text-xl text-foreground-950" />
              </span>
              <h3 className="font-heading text-lg md:text-xl text-foreground-950 font-bold">十八禁电影</h3>
              <p className="mt-1 text-xs text-foreground-600">
                收录 {movieCount} 部 · 惊悚 / 犯罪 / 爱情 / 文艺
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs text-accent-400">
                进入分类 <i className="ri-arrow-right-line" />
              </span>
            </div>
          </Link>

          <Link
            to="/category?type=tv"
            className="group relative h-40 md:h-48 rounded-lg overflow-hidden border border-background-200/70"
          >
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20dark%20tv%20screen%20glow%20in%20a%20dim%20living%20room%20at%20night%2C%20deep%20crimson%20and%20charcoal%20color%20grade%2C%20dramatic%20low-key%20lighting%2C%20no%20text&width=800&height=400&seq=sexcat-tv&orientation=landscape"
              alt="十八禁电视剧分类入口"
              title="十八禁电视剧分类入口"
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/60 to-transparent" />
            <div className="absolute inset-0 p-6 flex flex-col justify-center">
              <span className="w-11 h-11 rounded-md bg-accent-500/90 flex items-center justify-center mb-3">
                <i className="ri-tv-2-line text-xl text-background-50" />
              </span>
              <h3 className="font-heading text-lg md:text-xl text-foreground-950 font-bold">十八禁电视剧</h3>
              <p className="mt-1 text-xs text-foreground-600">
                收录 {tvCount} 部 · 都市 / 悬疑 / 历史 / 剧情
              </p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs text-accent-400">
                进入分类 <i className="ri-arrow-right-line" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* 最新上映 */}
      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <SectionHeading
          eyebrow="NEW RELEASE"
          title="最新上映"
          description="按上映时间倒序，抢先了解近期值得关注的十八禁影视新作。"
          moreTo="/category"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {latest.map((movie, i) => (
            <MovieCard key={movie.id} movie={movie} index={i} />
          ))}
        </div>
      </section>

      {/* 热门推荐 */}
      <section className="w-full bg-background-100/60 border-y border-background-200/60">
        <div className="w-full px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="MOST VIEWED"
            title="热门推荐"
            description="按站内浏览量排名，呈现最受成年观众关注的高热度作品。"
            moreTo="/category"
            moreLabel="查看热门榜"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {popular.map((movie, i) => (
              <div key={movie.id} className="relative">
                <span className="absolute -top-2 -left-2 z-10 w-8 h-8 rounded-md bg-primary-500 text-foreground-950 font-label font-bold flex items-center justify-center text-sm">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <MovieCard movie={movie} index={i} />
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-foreground-500 flex items-center gap-2">
            <i className="ri-fire-line text-accent-400" />
            热度数据基于站内演示统计，仅用于展示排序效果。
          </p>
        </div>
      </section>

      {/* 精选专题 */}
      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <SectionHeading
          eyebrow="EDITORIAL"
          title="精选专题"
          description="由编辑团队策划的片单合集，围绕主题与风格为你提供更具体的观看方向。"
          moreTo="/special"
          moreLabel="全部专题"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {specials.map((sp, i) => (
            <Link
              key={sp.id}
              to="/special"
              className="group block bg-background-100 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-500/60 transition-colors animate-fade-up"
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="relative w-full aspect-[16/9] overflow-hidden bg-background-300">
                <img
                  src={sp.cover}
                  alt={`${sp.title} 专题封面`}
                  title={`${sp.title} - 十八禁影视专题`}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 bg-accent-500 text-background-50 text-[11px] px-2 py-1 rounded-md font-label">
                  专题 {String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-heading text-base text-foreground-950 font-bold leading-snug group-hover:text-primary-400 transition-colors">
                  {sp.title}
                </h3>
                <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed line-clamp-3">{sp.summary}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs text-accent-400">
                  进入专题 <i className="ri-arrow-right-line" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 网站简介 */}
      <section className="w-full bg-primary-500/10 border-y border-primary-500/20">
        <div className="w-full px-4 md:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="font-label text-xs text-accent-400 tracking-[0.28em] mb-3">OUR MISSION</p>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-bold leading-tight">
              让每一次选择，都更接近你想看的故事
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              十八禁影视大全专注于成人向影视作品的整理与推荐。我们把影片信息拆解成清晰的区块——类型、年份、地区、主创与简介，
              让你在没有干扰的前提下，快速判断一部作品是否值得一看。这里没有在线播放，只有更诚实的信息与更用心的推荐。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm rounded-md px-5 py-2.5 whitespace-nowrap cursor-pointer transition-colors"
              >
                了解我们
              </Link>
              <Link
                to="/special"
                className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 border border-background-300/60 text-foreground-900 text-sm rounded-md px-5 py-2.5 whitespace-nowrap cursor-pointer transition-colors"
              >
                浏览专题
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {[
              { label: '影视作品', value: `${movies.length}+`, icon: 'ri-movie-2-line' },
              { label: '精选专题', value: `${specials.length}`, icon: 'ri-bookmark-3-line' },
              { label: '内容分区', value: '9', icon: 'ri-layout-grid-line' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-background-100 border border-background-200/70 rounded-lg p-4 md:p-5 text-center"
              >
                <span className="mx-auto w-10 h-10 rounded-md bg-secondary-100 flex items-center justify-center mb-3">
                  <i className={`${stat.icon} text-lg text-accent-400`} />
                </span>
                <p className="font-label text-2xl text-foreground-950 font-bold">{stat.value}</p>
                <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}